import type { Tenant } from '@shared/types/Tenant';
import { esc, fmtDate } from '@presentation/shell/format';
import {
  brandFontStyleBlock,
  brandHeaderTextHtml,
  brandFooterTextHtml,
  brandRootClass,
  brandRootStyleDecls,
  brandCenterStripHtml,
  bulletinHeaderLogoHtml,
  bulletinRightLogoHtml,
  documentThemeFor,
  type BrandTheme,
} from '@presentation/print/documentBrand';

export interface RapportGestionChamps {
  activite: string;
  faits: string;
  perspectives: string;
  rse: string;
  invest: string;
}

export interface ReglementInterieurChamps {
  date: string;
  discipline: string;
  hygiene: string;
  sanctions: string;
}

/** En-tête de charte des documents « texte » (vide, donc sans effet, tant qu'aucune charte n'existe). */
function brandedHeader(theme: BrandTheme, tenant: Tenant | null): string {
  if (!theme.active) return '';
  const nom = tenant?.raison_sociale || tenant?.nom || 'Entreprise';
  const bandeau = brandCenterStripHtml(theme, nom);
  const droite = bulletinRightLogoHtml(theme, nom);
  return `${bandeau ? `<div style="margin:-24px -24px 0 -24px;">${bandeau}</div>` : ''}<div class="bulletin-header" style="margin:${bandeau ? '0' : '-24px'} -24px 16px -24px;">
      <div>${bulletinHeaderLogoHtml(40, theme, nom)}<div class="company">${esc(nom)}</div><div class="meta">${[tenant?.adresse, tenant?.ville].filter(Boolean).map(esc).join(', ')}${brandHeaderTextHtml(theme)}</div></div>${droite ? `<div style="text-align:right;">${droite}</div>` : ''}
    </div>`;
}

function brandedFooter(theme: BrandTheme): string {
  if (!theme.active || !theme.footerText) return '';
  return `<div class="bulletin-footer" style="margin:16px -24px -24px -24px;">${brandFooterTextHtml(theme).replace(/^<br>/, '')}</div>`;
}

export function buildRapportGestionHTML(
  annee: string,
  champs: RapportGestionChamps,
  tenant: Tenant | null = null,
): string {
  const theme = documentThemeFor(tenant);
  return `<div class="card${brandRootClass(theme)}" style="${theme.active ? `${esc(brandRootStyleDecls(theme))};` : ''}padding:24px;background:#fff;">
      ${brandFontStyleBlock(theme)}${brandedHeader(theme, tenant)}<h1 style="color:var(--marine);">Rapport de gestion ${esc(annee)}</h1>
      <p><strong>Activité :</strong> ${esc(champs.activite)}</p>
      <p><strong>Faits marquants :</strong> ${esc(champs.faits)}</p>
      <p><strong>Perspectives :</strong> ${esc(champs.perspectives)}</p>
      <p><strong>RSE :</strong> ${esc(champs.rse)}</p>
      <p><strong>Investissements :</strong> ${esc(champs.invest)}</p>${brandedFooter(theme)}
    </div>`;
}

export function buildReglementInterieurHTML(
  champs: ReglementInterieurChamps,
  tenant: Tenant | null = null,
): string {
  const theme = documentThemeFor(tenant);
  return `<div class="card${brandRootClass(theme)}" style="${theme.active ? `${esc(brandRootStyleDecls(theme))};` : ''}padding:24px;background:#fff;">
      ${brandFontStyleBlock(theme)}${brandedHeader(theme, tenant)}<h1 style="color:var(--marine);">Règlement intérieur</h1>
      <p><strong>Entrée en vigueur :</strong> ${esc(fmtDate(champs.date))}</p>
      <p><strong>Discipline :</strong> ${esc(champs.discipline)}</p>
      <p><strong>Hygiène et sécurité :</strong> ${esc(champs.hygiene)}</p>
      <p><strong>Sanctions :</strong> ${esc(champs.sanctions)}</p>${brandedFooter(theme)}
    </div>`;
}
